import type {
  ExaminerTurn,
  ExaminerTurnProgress,
  SpeakingMode,
  SpeakingPart,
} from '@ielts-buddy/shared'
import { useRef } from 'react'
import { haptics } from '../../shared/telegram.js'
import {
  CloseIcon,
  EyeIcon,
  EyeOffIcon,
  MicIcon,
  StopIcon,
} from '../../shared/ui/icons.js'
import {
  Button,
  Card,
  ErrorState,
  IconButton,
  LoadingDots,
  ScreenHeader,
} from '../../shared/ui/index.js'
import {
  type ClientPhase,
  CUE_CARD_PREP_SECONDS,
  useSpeakingSession,
  WRAP_UP_WARNING_S,
} from './use-speaking-session.js'

/**
 * Speaking session screen.
 *
 * Flow (client-side state machine):
 *
 *   Boot:
 *     - call POST /speaking/sessions  -> { sessionId, examinerTurn (audio+text), phase }
 *     - play examiner audio, then transition to 'awaiting_answer'
 *
 *   Loop:
 *     - 'awaiting_answer' : show big mic button. User taps -> recorder.start()
 *     - 'recording'       : show stop button + live waveform
 *     - 'uploading'       : show "Examiner is thinking…" while POST /turn is in-flight
 *     - on 'turn' response, if phase === 'finished' -> jump to Finishing
 *                            else play new examiner audio, back to 'awaiting_answer'
 *
 *   Cue card phase ('cue_card_prep') is special:
 *     - Show the cue card and a 60-second countdown (circular ring + number)
 *     - When countdown hits 0 (or user taps "I'm ready"), auto-start recording
 *
 *   Finishing:
 *     - call POST /finish, then router.navigateToSpeakingResults(sessionId)
 *
 * UX notes:
 *   - Haptic feedback fires on every primary action (start/stop/skip/finish/error)
 *     so the app feels native on mobile.
 *   - Exit (X button) requires confirmation when a session is in progress —
 *     accidentally tapping X mid-test would otherwise abandon the user's work.
 *
 * All business logic lives in useSpeakingSession (features/speaking/use-speaking-session.ts).
 * This file is purely responsible for rendering.
 */

interface SpeakingScreenProps {
  mode: SpeakingMode
  part?: SpeakingPart
}

export function SpeakingScreen({ mode, part }: SpeakingScreenProps) {
  const {
    phase,
    sessionId,
    lastTranscript,
    partProgress,
    recordingElapsed,
    recordingCap,
    showExaminerText,
    recorderVolume,
    navigateHome,
    startRecording,
    stopAndUpload,
    retryTurn,
    skipTurn,
    handleClose,
    setShowExaminerText,
  } = useSpeakingSession({ mode, part })

  // ---------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------

  // Derive what the examiner is "doing" right now for the avatar animation.
  const examinerStatus: ExaminerAvatarStatus =
    phase.kind === 'examiner_speaking'
      ? 'speaking'
      : phase.kind === 'recording'
        ? 'listening'
        : 'idle'

  // Phases that have a current examiner question to optionally reveal.
  const currentTurn =
    'turn' in phase && phase.turn ? (phase.turn as ExaminerTurn) : null

  const canRevealText = !!currentTurn && phase.kind !== 'cue_card_prep'
  const canSkip =
    import.meta.env.DEV &&
    !!sessionId &&
    (phase.kind === 'awaiting_answer' ||
      phase.kind === 'recording' ||
      phase.kind === 'cue_card_prep' ||
      phase.kind === 'examiner_speaking')

  return (
    <div className="min-h-full screen-padding flex flex-col gap-5 animate-fade-in">
      <ScreenHeader
        leading={
          <IconButton aria-label="Close" onClick={handleClose}>
            <CloseIcon className="w-4 h-4" />
          </IconButton>
        }
        center={
          <div className="flex flex-col items-center gap-1 w-full">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-brand-text-muted">
              {sessionLabel(mode)}
            </span>
            <SegmentedProgress
              partProgress={partProgress}
              mode={mode}
              sectionPart={part}
            />
          </div>
        }
        trailing={
          <IconButton
            aria-label={
              showExaminerText ? 'Hide examiner text' : 'Show examiner text'
            }
            aria-pressed={showExaminerText}
            title="Couldn't catch the examiner? Show text"
            variant={showExaminerText ? 'accent' : 'default'}
            disabled={!canRevealText}
            className={!canRevealText ? 'opacity-40' : ''}
            onClick={() => {
              haptics.select()
              setShowExaminerText((v) => !v)
            }}
          >
            {showExaminerText ? (
              <EyeOffIcon className="w-4 h-4" />
            ) : (
              <EyeIcon className="w-4 h-4" />
            )}
          </IconButton>
        }
      />

      <PhaseHeader phase={phase} />

      <div className="flex-1 flex flex-col items-center justify-center gap-7 text-center">
        {phase.kind === 'starting' && (
          <>
            <ExaminerAvatar status="idle" />
            <LoadingDots label="Connecting to your examiner…" />
          </>
        )}

        {(phase.kind === 'examiner_speaking' ||
          phase.kind === 'awaiting_answer' ||
          phase.kind === 'recording' ||
          phase.kind === 'uploading') && (
          <>
            <ExaminerAvatar status={examinerStatus} />
            {showExaminerText && currentTurn && (
              <ExaminerTextReveal text={currentTurn.text} />
            )}
            {phase.kind === 'awaiting_answer' && (
              <MicButton mode="idle" onClick={startRecording} />
            )}
            {phase.kind === 'recording' && (
              <>
                <RecordingMeter
                  elapsed={recordingElapsed}
                  cap={recordingCap}
                  part={phase.turn.part}
                />
                <MicButton
                  mode="recording"
                  volume={recorderVolume}
                  onClick={stopAndUpload}
                />
              </>
            )}
            {phase.kind === 'uploading' && (
              <LoadingDots label="Examiner is thinking…" size="sm" />
            )}
          </>
        )}

        {phase.kind === 'cue_card_prep' && (
          <>
            <CueCardView turn={phase.turn} />
            <CueCardTimer
              secondsLeft={phase.secondsLeft}
              total={CUE_CARD_PREP_SECONDS}
              onReady={startRecording}
            />
          </>
        )}

        {phase.kind === 'turn_error' && (
          <div className="space-y-4 max-w-md w-full">
            <Card tone="error-soft" className="p-4 text-center space-y-1">
              <div className="text-brand-error font-semibold">
                Couldn't process your answer
              </div>
              <div className="text-brand-text-muted text-sm">
                {phase.message}
              </div>
              <div className="text-brand-text-subtle text-xs">
                The session is still active — re-record this answer to continue.
              </div>
            </Card>
            <MicButton mode="idle" caption="Try again" onClick={retryTurn} />
            <Button
              variant="subtle"
              size="sm"
              className="w-full"
              onClick={handleClose}
            >
              Or exit to home
            </Button>
          </div>
        )}

        {phase.kind === 'finishing' && (
          // Cycling phases instead of a static label — the scoring wait is
          // 5–10s and a single dot animation makes that feel longer than it
          // is. Each phrase mirrors a real step the backend is doing, so the
          // user gets a productive-feeling progress narrative AND learns what
          // goes into a band score.
          <LoadingDots
            phases={[
              'Analysing your answers…',
              'Analysing fluency & coherence…',
              'Checking lexical resource…',
              'Scoring grammar & pronunciation…',
              'Drafting your examiner feedback…',
            ]}
            phaseIntervalMs={1600}
            hint="This usually takes 5–10 seconds."
          />
        )}

        {phase.kind === 'error' && (
          <ErrorState
            title="Something went wrong"
            message={phase.message}
            primaryAction={{ label: 'Back to home', onClick: navigateHome }}
          />
        )}
      </div>

      {lastTranscript && (
        <Card size="flat" tone="primary-soft" className="p-4">
          <div className="text-[11px] uppercase tracking-wider text-brand-primary font-semibold mb-1">
            You just said
          </div>
          <div className="text-sm text-brand-text line-clamp-4 leading-relaxed">
            {lastTranscript}
          </div>
        </Card>
      )}

      {canSkip && (
        <button
          onClick={skipTurn}
          aria-label="Skip turn (dev)"
          className="self-center text-brand-text-subtle active:opacity-50 px-3 py-1 rounded border border-dashed border-brand-border text-[10px] uppercase tracking-wider"
        >
          Skip (dev)
        </button>
      )}
    </div>
  )
}

/**
 * Three thin bars side-by-side, one per IELTS part. The current part fills
 * proportionally with how many of its questions the candidate has finished;
 * passed parts are 100% filled; future parts are empty. This is much more
 * useful than a single continuous bar — the candidate always knows which
 * part they're in.
 *
 * The fill is driven by the server's `progress` payload (part + indexInPart
 * + totalInPart) so we never have to know the script structure on the
 * client. Earlier this was computed from `questionIndex / TOTAL_TURNS`,
 * which had an off-by-one against the actual script length and could
 * "regress" the bar at part boundaries.
 */
function SegmentedProgress({
  partProgress,
  mode,
  sectionPart,
}: {
  partProgress: ExaminerTurnProgress | null
  mode: SpeakingMode
  sectionPart?: SpeakingPart
}) {
  // Only show bars for the parts actually in this session.
  // drill → Part 1 only; section → the specific part; full_test → all three.
  // Showing all 3 bars for a drill or Part 2 Focus would either leave empty
  // bars (confusing) or fake 100% fills for parts the candidate never did
  // (actively misleading).
  const partsToShow: (1 | 2 | 3)[] =
    mode === 'full_test' ? [1, 2, 3] : [(sectionPart ?? 1) as 1 | 2 | 3]

  return (
    <div className="w-full flex items-center gap-1.5">
      {partsToShow.map((part) => {
        let fill = 0
        let isCurrent = false
        if (partProgress) {
          isCurrent = partProgress.part === part
          if (mode === 'full_test' && part < partProgress.part) {
            // Only pre-fill "already done" parts in a full test where the
            // candidate actually completed those parts. For section modes the
            // session starts in the middle of the part numbering, so filling
            // previous bars would show fake completion.
            fill = 1
          } else if (part === partProgress.part) {
            // The just-delivered turn is "in progress" from the candidate's
            // POV — treat it as not-yet-completed so the bar only ticks up
            // when they actually move on.
            fill = partProgress.indexInPart / partProgress.totalInPart
          }
        }
        return (
          <div key={part} className="flex-1 flex flex-col items-center gap-1">
            <div className="w-full h-1 rounded-full bg-brand-surface-2 overflow-hidden">
              <div
                className="h-full w-full bg-brand-primary origin-left transition-transform duration-500"
                style={{ transform: `scaleX(${fill})` }}
              />
            </div>
            <div
              className={`text-[10px] uppercase tracking-wider font-semibold ${
                isCurrent ? 'text-brand-primary' : 'text-brand-text-subtle'
              }`}
            >
              {partsToShow.length > 1
                ? `Part ${part}`
                : mode === 'warm_up'
                  ? 'Warm Up'
                  : `Part ${part} Focus`}
            </div>
          </div>
        )
      })}
    </div>
  )
}

function PhaseHeader({ phase }: { phase: ClientPhase }) {
  const labels: Record<ClientPhase['kind'], string> = {
    starting: 'Connecting',
    examiner_speaking: 'Listen carefully',
    awaiting_answer: 'Your turn — speak now',
    cue_card_prep: 'Preparation time',
    recording: 'Recording your answer',
    uploading: 'Processing',
    turn_error: 'Try again',
    finishing: 'Finishing up',
    error: 'Error',
  }
  // A small breathing dot before the label that pulses when the user is
  // actively engaged (recording / examiner speaking).
  const isLive =
    phase.kind === 'recording' ||
    phase.kind === 'examiner_speaking' ||
    phase.kind === 'cue_card_prep'
  const dotColor =
    phase.kind === 'recording'
      ? 'bg-brand-error'
      : phase.kind === 'examiner_speaking'
        ? 'bg-brand-primary'
        : phase.kind === 'cue_card_prep'
          ? 'bg-brand-warning'
          : 'bg-brand-text-subtle'

  return (
    <div className="text-center flex items-center justify-center gap-2">
      <span
        aria-hidden
        className={`inline-block w-1.5 h-1.5 rounded-full ${dotColor} ${
          isLive ? 'animate-pulse' : ''
        }`}
      />
      <div className="text-xs uppercase tracking-wider text-brand-text-muted font-medium">
        {labels[phase.kind]}
      </div>
    </div>
  )
}

function sessionLabel(mode: SpeakingMode): string {
  if (mode === 'full_test') return 'Speaking Mock'
  return 'Quick Practice'
}

// ---------------------------------------------------------------------------
// Examiner avatar — animated halo conveys what she's doing
// ---------------------------------------------------------------------------

type ExaminerAvatarStatus = 'speaking' | 'listening' | 'idle'

function ExaminerAvatar({ status }: { status: ExaminerAvatarStatus }) {
  // Squircle (rounded-3xl) shape mirrors the icon container on the Home
  // Speaking CTA — keeps a single shape language across the app for primary
  // visual elements. The pulse / halo decorations match the same shape so
  // they scale cleanly without a circle-on-square mismatch.
  return (
    <div className="relative w-44 h-44 flex items-center justify-center">
      {status === 'speaking' && (
        <>
          <span className="absolute inset-0 rounded-3xl bg-brand-primary opacity-20 animate-[ping_1.6s_cubic-bezier(0,0,0.2,1)_infinite]" />
          <span
            className="absolute inset-2 rounded-3xl bg-brand-primary opacity-25 animate-[ping_1.6s_cubic-bezier(0,0,0.2,1)_infinite]"
            style={{ animationDelay: '0.4s' }}
          />
        </>
      )}
      {status === 'listening' && (
        <span className="absolute -inset-1 rounded-[28px] border-2 border-brand-primary/60 animate-pulse" />
      )}

      <div className="relative w-32 h-32 rounded-3xl bg-brand-gradient-hero text-white flex items-center justify-center shadow-brand-lg overflow-hidden">
        {/* Inner light sweep — simulates ambient light catching the top-left
            edge, matching the inset highlight on hero cards elsewhere. */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/12 via-transparent to-transparent pointer-events-none" />
        {/* Decorative corner brackets — a subtle editorial detail that frames
            the monogram without adding visual noise. */}
        <span
          aria-hidden
          className="absolute top-2.5 left-2.5 w-3 h-3 border-l border-t border-white/25"
        />
        <span
          aria-hidden
          className="absolute bottom-2.5 right-2.5 w-3 h-3 border-r border-b border-white/25"
        />
        <span className="relative font-sans font-bold text-5xl text-white leading-none select-none">
          S
        </span>
      </div>
    </div>
  )
}

function ExaminerTextReveal({ text }: { text: string }) {
  return (
    <Card
      size="flat"
      tone="primary-soft"
      className="w-full max-w-md p-4 text-left"
    >
      <div className="text-[11px] uppercase tracking-wider text-brand-primary font-semibold mb-1">
        Sarah said
      </div>
      <p className="text-sm leading-relaxed text-brand-text">{text}</p>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Cue card + circular prep timer
// ---------------------------------------------------------------------------

function CueCardView({ turn }: { turn: ExaminerTurn }) {
  const card = turn.cueCard
  if (!card) return null
  // Gradient hero treatment — matches the Home module cards. Cue card is the
  // most important surface during Part 2 (the candidate's "task at hand"),
  // so it earns the visual weight. Internal elements switch to white-on-
  // translucent so they read on the dark gradient.
  return (
    <Card size="hero" tone="gradient" className="w-full max-w-md p-5 text-left">
      {/* Single section identifier — the pill already says "Cue card · Part 2",
          so the separate "2" badge that used to sit beside it was just visual
          repetition. One stronger pill carries the role solo. */}
      <div className="mb-3">
        <span className="text-[11px] uppercase tracking-wider px-2.5 py-1 rounded-full bg-white/20 font-semibold">
          Cue card · Part 2
        </span>
      </div>
      <div className="text-lg font-semibold mb-4 leading-snug tracking-tight">
        {card.topic}
      </div>
      <div className="text-[11px] uppercase tracking-wider text-white/70 font-semibold mb-2">
        You should say
      </div>
      <ul className="text-sm space-y-1.5 mb-1 text-white/90">
        {card.bullets.map((b, i) => (
          <li key={i} className="flex gap-2">
            <span aria-hidden className="text-white/60 font-bold">
              ·
            </span>
            <span>{b}</span>
          </li>
        ))}
      </ul>
    </Card>
  )
}

function CueCardTimer({
  secondsLeft,
  total,
  onReady,
}: {
  secondsLeft: number
  total: number
  onReady(): void
}) {
  const radius = 36
  const circumference = 2 * Math.PI * radius
  const elapsedFraction = 1 - secondsLeft / total
  const dashOffset = circumference * elapsedFraction

  return (
    <div className="flex flex-col items-center gap-3">
      <button
        onClick={onReady}
        aria-label={`I'm ready to speak (${secondsLeft} seconds prep left)`}
        className="relative w-24 h-24 rounded-full bg-brand-surface border border-brand-border shadow-brand flex items-center justify-center active:scale-95 transition-transform"
      >
        <svg
          viewBox="0 0 88 88"
          className="absolute inset-0 -rotate-90"
          aria-hidden
        >
          <circle
            cx="44"
            cy="44"
            r={radius}
            fill="none"
            stroke="var(--brand-border)"
            strokeWidth="6"
          />
          <circle
            cx="44"
            cy="44"
            r={radius}
            fill="none"
            stroke="var(--brand-primary)"
            strokeWidth="6"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            style={{ transition: 'stroke-dashoffset 1s linear' }}
          />
        </svg>
        <div className="relative flex flex-col items-center">
          <div className="text-2xl font-semibold text-brand-text leading-none">
            {secondsLeft}
          </div>
          <div className="text-[10px] uppercase tracking-wider text-brand-text-muted">
            sec left
          </div>
        </div>
      </button>
      <div className="text-xs text-brand-text-muted">
        Tap when you're ready · or wait for the timer
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Recording meter — mm:ss / cap with colour-coded progress bar
//
// Mirrors how a real IELTS examiner times each turn. The cap is the per-part
// IELTS frame (45s / 120s / 90s); when elapsed crosses the warning band the
// bar shifts amber, then red in the final 3 seconds. For Part 2 we also
// show a dot at the 1-min mark to indicate the IELTS minimum monologue
// length ("you should speak for one to two minutes").
// ---------------------------------------------------------------------------

function RecordingMeter({
  elapsed,
  cap,
  part,
}: {
  elapsed: number
  cap: number
  part: 1 | 2 | 3
}) {
  const remaining = Math.max(0, cap - elapsed)
  const fraction = Math.min(1, elapsed / cap)
  const isCritical = remaining <= 3
  const isWarning = !isCritical && remaining <= WRAP_UP_WARNING_S

  const fmt = (s: number) => {
    const m = Math.floor(s / 60)
    const sec = Math.floor(s % 60)
      .toString()
      .padStart(2, '0')
    return `${m}:${sec}`
  }

  // Part 2: candidates are scored against "1-2 minutes". Surface the 1-min
  // minimum as a small tick on the bar so a candidate winding down too early
  // can see they should keep going.
  const showMinMark = part === 2
  const minMarkFraction = 60 / cap

  const elapsedClass = isCritical
    ? 'text-brand-error font-semibold'
    : isWarning
      ? 'text-brand-warning font-semibold'
      : 'text-brand-text'
  const barClass = isCritical
    ? 'bg-brand-error'
    : isWarning
      ? 'bg-brand-warning'
      : 'bg-brand-primary'

  return (
    <div className="flex flex-col items-center gap-1.5" aria-live="polite">
      <div className="flex items-baseline gap-1 font-mono tabular-nums text-sm">
        <span className={elapsedClass}>{fmt(elapsed)}</span>
        <span className="text-brand-text-subtle">/ {fmt(cap)}</span>
      </div>
      <div className="relative w-40 h-1 rounded-full bg-brand-surface-2 overflow-hidden">
        <div
          className={`h-full w-full rounded-full origin-left transition-[transform,background-color] duration-200 ${barClass}`}
          style={{ transform: `scaleX(${fraction})` }}
        />
        {showMinMark && (
          <span
            aria-hidden
            title="IELTS minimum (1 min)"
            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-0.5 h-2 bg-brand-text-subtle"
            style={{ left: `${minMarkFraction * 100}%` }}
          />
        )}
      </div>
      {(isWarning || isCritical) && (
        <div className="text-[11px] uppercase tracking-wider text-brand-warning font-semibold">
          {isCritical ? 'Wrap up now' : 'Almost time'}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Mic button — laconic, animated, with a scrolling waveform when recording
// ---------------------------------------------------------------------------

function MicButton({
  mode,
  volume = 0,
  caption,
  onClick,
}: {
  mode: 'idle' | 'recording'
  volume?: number
  caption?: string
  onClick(): void
}) {
  const recording = mode === 'recording'
  const ringScale = recording ? 1 + volume * 0.6 : 1
  const label = caption ?? (recording ? 'Tap to stop' : 'Tap to speak')

  return (
    <div className="flex flex-col items-center gap-4">
      {recording && <Waveform volume={volume} />}
      <button
        onClick={onClick}
        aria-label={label}
        className="relative w-24 h-24 flex items-center justify-center active:scale-95 transition-transform"
      >
        {/* Pulse / amplitude rings match the squircle shape so they read
            as part of the same icon family as the Home Speaking CTA. */}
        {!recording && (
          <span className="absolute inset-0 rounded-2xl bg-brand-primary opacity-30 animate-ping" />
        )}
        {recording && (
          <span
            className="absolute inset-0 rounded-2xl bg-brand-error/40 transition-transform duration-75"
            style={{ transform: `scale(${ringScale})` }}
          />
        )}
        <span
          className={`relative w-20 h-20 rounded-2xl flex items-center justify-center shadow-brand-lg text-brand-on-primary ${
            recording ? 'bg-brand-error' : 'bg-brand-gradient-hero'
          }`}
        >
          {recording ? (
            <StopIcon className="w-8 h-8" />
          ) : (
            <MicIcon className="w-9 h-9" />
          )}
        </span>
      </button>
      <div className="text-xs text-brand-text-muted font-medium">{label}</div>
    </div>
  )
}

/**
 * Scrolling 32-bar waveform driven by the live volume samples. Each render
 * appends the latest volume to a ring buffer in a ref and we redraw the bars
 * — same idea as iMessage / WhatsApp voice messages. Pure visual sugar; if
 * the buffer is empty the row is a flat line (which is itself a useful
 * "we're recording but we don't hear you" signal).
 */
const WAVEFORM_BARS = 32
function Waveform({ volume }: { volume: number }) {
  const samplesRef = useRef<number[]>(new Array(WAVEFORM_BARS).fill(0))
  // Push the new sample into the ring (in-place — fine because we re-render
  // every frame anyway via the volume prop).
  samplesRef.current.push(volume)
  if (samplesRef.current.length > WAVEFORM_BARS) {
    samplesRef.current.shift()
  }
  const samples = samplesRef.current

  return (
    <div aria-hidden className="flex items-end gap-[3px] h-12 w-56">
      {samples.map((v, i) => {
        // Boost low volumes so the bars feel responsive even at conversational level.
        const boosted = Math.min(1, v ** 0.5)
        const heightPct = 12 + boosted * 88
        return (
          <div
            key={i}
            className="flex-1 rounded-full bg-brand-error/80 transition-[height] duration-75"
            style={{ height: `${heightPct}%` }}
          />
        )
      })}
    </div>
  )
}
